import { useCallback } from 'react';
import { useAppInsightsContext } from '@microsoft/applicationinsights-react-js';
import { useApplicationInsightsContext } from '../providers/ApplicationInsightsProvider';
import { ICustomProperties, AuthClaims } from '../services/ReactInsightsLogger';

export interface UseApplicationInsightsHook {
    // Core logging methods
    logPageView: (name?: string, url?: string) => void;
    logEvent: (name: string, properties?: ICustomProperties) => void;
    logMetric: (name: string, average: number, properties?: ICustomProperties) => void;
    logException: (exception: Error, severityLevel?: number, customProperties?: ICustomProperties) => void;
    logTrace: (message: string, properties?: ICustomProperties) => void;
    
    // React-specific convenience methods
    trackEvent: (name: string, properties?: ICustomProperties) => void;
    trackException: (exception: Error, customProperties?: ICustomProperties) => void;
    trackUserAction: (action: string, component?: string, properties?: ICustomProperties) => void;
    
    // User context management
    setUserContext: (claims: undefined | AuthClaims) => void;
    logBrowserCapabilities: () => void;
    
    // Utility properties
    isInitialized: boolean;
}

export const useApplicationInsights = (): UseApplicationInsightsHook => {
    // Get Microsoft's react plugin (can be used for advanced tracking)
    const reactPlugin = useAppInsightsContext();
    
    // Get our custom logger and initialization state
    const { logger, isInitialized } = useApplicationInsightsContext();

    const logPageView = useCallback((name?: string, url?: string) => {
        logger?.logPageView(name, url);
    }, [logger]);

    const logEvent = useCallback((name: string, properties?: ICustomProperties) => {
        logger?.logEvent(name, properties);
    }, [logger]);

    const logMetric = useCallback((name: string, average: number, properties?: ICustomProperties) => {
        logger?.logMetric(name, average, properties);
    }, [logger]);

    const logException = useCallback((exception: Error, severityLevel?: number, customProperties?: ICustomProperties) => {
        logger?.logException(exception, severityLevel, customProperties);
    }, [logger]);

    const logTrace = useCallback((message: string, properties?: ICustomProperties) => {
        logger?.logTrace(message, properties);
    }, [logger]);

    const setUserContext = useCallback((claims: undefined | AuthClaims) => {
        logger?.setUserContext(claims);
    }, [logger]);

    const logBrowserCapabilities = useCallback(() => {
        logger?.logBrowserCapabilities();
    }, [logger]);

    // React-specific convenience methods
    const trackEvent = useCallback((name: string, properties?: ICustomProperties) => {
        logEvent(name, properties);
    }, [logEvent]);

    const trackException = useCallback((exception: Error, customProperties?: ICustomProperties) => {
        logException(exception, 2, customProperties); // SeverityLevel.Error = 2
    }, [logException]);

    const trackUserAction = useCallback((action: string, component?: string, properties?: ICustomProperties) => {
        const eventProperties = {
            action,
            component,
            ...properties
        };
        logEvent('UserAction', eventProperties);
    }, [logEvent]);

    return {
        logPageView,
        logEvent,
        logMetric,
        logException,
        logTrace,
        trackEvent,
        trackException,
        trackUserAction,
        setUserContext,
        logBrowserCapabilities,
        isInitialized
    };
};